import {
	OrderListInput,
	type OrderListItem,
	type OrderListQuery,
	OrderStatus,
	PaymentStatus,
	ShipmentStatus,
} from "@cleanbudget/contract/order";
import { IconFileInvoice } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ButtonLink } from "@/components/button-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { formatDateTime } from "@/lib/format";
import { formatCents } from "@/lib/money";
import { orpc } from "@/lib/orpc";
import {
	OrderStatusBadge,
	OrderStatuses,
	PaymentStatusBadge,
	ShipmentStatusBadge,
} from "./-components/order-badges";

const ordersQuery = (query: OrderListQuery) => orpc.order.list.queryOptions({ input: query });

/** The list's defaults, left out of the URL so a plain `/order` is the default view. */
const { sort, order, page, pageSize } = OrderListInput.parse({});

/**
 * Orders (#99): every order, newest first. Search (number, name, email, company), the three status
 * filters, sort and page live in the URL. On a phone: number, customer and amount, with the
 * statuses under the number.
 */
export const Route = createFileRoute("/_admin/order/")({
	validateSearch: OrderListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(ordersQuery(deps)),
	component: OrdersPage,
});

function OrdersPage() {
	const { t } = useTranslation("order");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(ordersQuery(search));

	const columns: readonly Column<OrderListItem>[] = [
		{
			id: "orderNumber",
			header: t("column.number"),
			sortable: true,
			cell: (item) => (
				<span className="flex flex-col gap-1.5">
					<Link
						to="/order/$orderId"
						params={{ orderId: item.id }}
						className="font-medium tabular-nums hover:underline"
					>
						{item.orderNumber}
					</Link>
					<span className="text-xs text-muted-foreground md:hidden">
						<time dateTime={item.placedAt}>{formatDateTime(item.placedAt)}</time>
					</span>
					<span className="md:hidden">
						<OrderStatuses order={item} />
					</span>
				</span>
			),
		},
		{
			id: "placedAt",
			header: t("column.placedAt"),
			sortable: true,
			secondary: true,
			cell: (item) => <time dateTime={item.placedAt}>{formatDateTime(item.placedAt)}</time>,
		},
		{
			id: "name",
			header: t("column.customer"),
			sortable: true,
			cell: (item) => (
				<span className="flex min-w-0 flex-col whitespace-normal">
					<span className="font-medium break-words">{item.name}</span>
					{item.company ? <span className="text-xs break-words">{item.company}</span> : null}
					<span className="text-xs break-all text-muted-foreground">{item.email}</span>
				</span>
			),
		},
		{
			id: "total",
			header: t("column.total"),
			sortable: true,
			align: "right",
			cell: (item) => <span className="tabular-nums">{formatCents(item.totalIncl)}</span>,
		},
		{
			id: "payment",
			header: t("column.payment"),
			secondary: true,
			cell: (item) => <PaymentStatusBadge status={item.paymentStatus} />,
		},
		{
			id: "shipment",
			header: t("column.shipment"),
			secondary: true,
			cell: (item) => <ShipmentStatusBadge status={item.shipmentStatus} />,
		},
		{
			id: "status",
			header: t("column.status"),
			secondary: true,
			cell: (item) => <OrderStatusBadge status={item.orderStatus} />,
		},
	];

	const filtered =
		Boolean(search.search) ||
		search.paymentStatus !== undefined ||
		search.shipmentStatus !== undefined ||
		search.orderStatus !== undefined ||
		search.toShip !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("title")}
				description={t("description")}
				actions={
					<ButtonLink to="/order/invoices" variant="outline">
						<IconFileInvoice aria-hidden />
						{t("openInvoices")}
					</ButtonLink>
				}
			/>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="paymentStatus"
						label={t("paymentStatus.label")}
						value={search.paymentStatus}
						options={PaymentStatus.options.map((status) => ({
							value: status,
							label: t(`paymentStatus.${status}`),
						}))}
					/>
					<FilterSelect
						param="shipmentStatus"
						label={t("shipmentStatus.label")}
						value={search.shipmentStatus}
						options={ShipmentStatus.options.map((status) => ({
							value: status,
							label: t(`shipmentStatus.${status}`),
						}))}
					/>
					<FilterSelect
						param="orderStatus"
						label={t("orderStatus.label")}
						value={search.orderStatus}
						options={OrderStatus.options.map((status) => ({
							value: status,
							label: t(`orderStatus.${status}`),
						}))}
					/>
					<FilterSelect
						param="toShip"
						label={t("toShipFilter.label")}
						value={search.toShip}
						options={[{ value: "yes", label: t("toShipFilter.yes") }]}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(item) => item.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
